import React, {
  ChangeEvent,
  FC,
  ReactElement,
  useCallback,
  useState,
} from 'react';
import { Button, CloseIcon } from '@cherre-frontend/ui';
import { ModelSuffixAndPrefixModalProps } from './model';
import {
  ButtonsContainer,
  Description,
  FieldsContainer,
  HeaderContainer,
  InputDescription,
  InputLabel,
  InputsContainer,
  PreviewContainer,
  PreviewContent,
  StyledCloseButton,
  StyledDialog,
  StyledTextField,
  Title,
} from './styles';

const ModelSuffixAndPrefixModal: FC<ModelSuffixAndPrefixModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValue,
}): ReactElement => {
  const [form, setForm] = useState(initialValue);

  const handleInputOnChange = useCallback(
    (
      label: string,
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void => {
      form[label] = event.target.value;
      setForm({ ...form });
    },
    [form]
  );

  const handleOnSave = useCallback((): void => {
    onSave(form);
    onClose();
  }, [form]);

  return (
    <StyledDialog maxWidth='sm' onClose={onClose} open={isOpen}>
      <HeaderContainer>
        <Title>Configure Table Name Prefixes & Suffixes</Title>
        <StyledCloseButton onClick={onClose}>
          <CloseIcon sx={{ height: '24px', width: '24px', color: '#9E9E9E' }} />
        </StyledCloseButton>
      </HeaderContainer>

      <Description>
        Add optional prefixes and/or suffixes to your target table names. This
        can help with organization and consistency in your data warehouse. This
        will apply to every table in your selected model.
      </Description>

      <InputsContainer>
        <FieldsContainer>
          <InputLabel>Prefix (Optional):</InputLabel>
          <InputDescription>
            Text added to the beginning of each generated table name.
          </InputDescription>
          <StyledTextField
            value={form['prefix']}
            placeholder='Enter Prefix'
            onChange={(event) => handleInputOnChange('prefix', event)}
          />
        </FieldsContainer>

        <FieldsContainer>
          <InputLabel>Suffix (Optional):</InputLabel>
          <InputDescription>
            Text added to the end of each generated table name.
          </InputDescription>
          <StyledTextField
            value={form['suffix']}
            placeholder='Enter Suffix'
            onChange={(event) => handleInputOnChange('suffix', event)}
          />
        </FieldsContainer>
      </InputsContainer>

      <PreviewContainer>
        <InputLabel>Preview:</InputLabel>

        <PreviewContent>
          <span>{form['prefix'] || '[Entered Prefix]'}</span>
          <span>_table_name_</span>
          <span>{form['suffix'] || '[Entered Suffix]'}</span>
        </PreviewContent>
      </PreviewContainer>

      <ButtonsContainer>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleOnSave} variant='contained'>
          Apply
        </Button>
      </ButtonsContainer>
    </StyledDialog>
  );
};

export default ModelSuffixAndPrefixModal;
